import React, { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Edit2, Check, X, LayoutGrid, Columns, RectangleVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DraggableQuestionCard from './DraggableQuestionCard';

/**
 * Draggable section containing questions
 */
const DraggableSection = ({
  sectionId,
  sectionName,
  questions,
  isCustomizeMode,
  onRenameSection,
  onSetSectionColumns,
  sectionColumns = 1,
  formDefinition,
  totalQuestions,
  onExport
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(sectionName);

  const {
    attributes,
    listeners,
    setNodeRef: setSectionNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: `section-${sectionId}`,
    data: {
      type: 'section',
      sectionId
    }
  });

  const { setNodeRef: setDroppableRef } = useDroppable({
    id: sectionId,
    data: {
      type: 'section',
      sectionId
    }
  });

  const sectionStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleRename = () => {
    if (editName.trim() && editName !== sectionName) {
      onRenameSection(sectionName, editName.trim());
    } else {
      setEditName(sectionName);
    }
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setEditName(sectionName);
      setIsEditing(false);
    }
  };

  const renderQuestion = (question) => {
    // Calculate actual question number by counting non-statement questions before this one
    const actualQuestionNumber = formDefinition?.fields 
      ? formDefinition.fields
          .slice(0, (question.position || 0) + 1)
          .filter(f => f.type !== 'statement').length
      : (question.position || 0) + 1;
    
    return (
      <DraggableQuestionCard
        key={question.fieldId}
        question={question}
        index={actualQuestionNumber - 1} // QuestionCard adds 1, so subtract 1 here
        totalQuestions={totalQuestions}
        onExport={onExport}
        isCustomizeMode={isCustomizeMode}
        sectionId={sectionId}
      />
    );
  };

  if (questions.length === 0 && !isCustomizeMode) {
    return null; // Don't render empty sections in view mode
  }

  return (
    <div
      ref={setSectionNodeRef}
      style={sectionStyle}
      className={`${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="mb-6">
        {/* Section Header */}
        <div className="flex items-center gap-2 mb-3">
          {isCustomizeMode && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded opacity-50 hover:opacity-100 transition-opacity"
              title="Drag to reorder section"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          
          {isEditing ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={handleKeyPress}
                className="text-sm font-medium uppercase tracking-wide"
                autoFocus
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRename}
                className="p-1 h-6 w-6"
              >
                <Check className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditName(sectionName);
                  setIsEditing(false);
                }}
                className="p-1 h-6 w-6"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                {sectionName}
              </h4>
              {isCustomizeMode && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditing(true)}
                  className="p-1 h-6 w-6 opacity-50 hover:opacity-100"
                >
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
          
          {/* Column layout controls */}
          {isCustomizeMode && (
            <div className="flex items-center gap-1 mr-2">
              <Button
                size="sm"
                variant={sectionColumns === 1 ? "secondary" : "ghost"}
                onClick={() => onSetSectionColumns(sectionName, 1)}
                className="p-1 h-6 w-6"
                title="1 column"
              >
                <RectangleVertical className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant={sectionColumns === 2 ? "secondary" : "ghost"}
                onClick={() => onSetSectionColumns(sectionName, 2)}
                className="p-1 h-6 w-6"
                title="2 columns"
              >
                <Columns className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant={sectionColumns === 3 ? "secondary" : "ghost"}
                onClick={() => onSetSectionColumns(sectionName, 3)}
                className="p-1 h-6 w-6"
                title="3 columns"
              >
                <LayoutGrid className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        {/* Questions Container */}
        <div
          ref={setDroppableRef}
          className={`min-h-[60px] grid gap-4 ${
            sectionColumns === 1 
              ? 'grid-cols-1' 
              : sectionColumns === 2 
                ? 'grid-cols-1 md:grid-cols-2' 
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          } ${
            isCustomizeMode ? 'border-2 border-dashed border-transparent hover:border-muted-foreground/20 rounded-lg p-2' : ''
          } ${questions.length === 0 && isCustomizeMode ? 'bg-muted/30' : ''}`}
        >
          {questions.length > 0 ? (
            <SortableContext 
              items={questions.map(q => `${sectionId}-${q.fieldId}`)}
              strategy={rectSortingStrategy}
            >
              {questions.map(renderQuestion)}
            </SortableContext>
          ) : (
            isCustomizeMode && (
              <div className="text-center text-muted-foreground text-sm py-8 col-span-full">
                Drop questions here
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default DraggableSection;